import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import { getServerURL } from '../../../utils/funcs/get-serverURL';
import './EICRForm.css';
import { BottomSpacer } from '../../spacer';

const getWorkDurationLabel = (work) => {
  if (
    typeof work.durationMins !== 'number' ||
    Number.isNaN(work.durationMins)
  ) {
    return '';
  }

  if (work.durationMins % 60 === 0) {
    const hours = work.durationMins / 60;
    return `${hours} hr${hours === 1 ? '' : 's'}`;
  }

  return `${work.durationMins} mins`;
};

const EICRForm = () => {
  const { getToken } = useAuth();
  const API_URL = getServerURL();

  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [approvingId, setApprovingId] = useState('');

  // eslint-disable-next-line no-unused-vars
  const [pendingApprovalsLoading, setPendingApprovalsLoading] =
    useState(false);

  // eslint-disable-next-line no-unused-vars
  const [pendingApprovalsError, setPendingApprovalsError] =
    useState('');

  // eslint-disable-next-line no-unused-vars
  const [pendingApprovals, setPendingApprovals] = useState([]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [, setApprovalRequired] = useState(false);

  const [remedialWorks, setRemedialWorks] = useState({});
  const [remedialWorksSearch, setRemedialWorksSearch] =
    useState('');

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',

    customerAddress: {
      line1: '',
      line2: '',
      city: '',
      postcode: ''
    },

    propertyType: 'Residential',

    propertyAddress: {
      line1: '',
      line2: '',
      city: '',
      postcode: ''
    },

    sameAsCustomer: true,

    inspectionDate: '',
    expiryDate: '',
    result: 'Pass',

    eicrFile: null,

    remedialWorkIds: []
  });

  /* ================================
     Custom Work
     ================================ */

  const [customWorkDescription, setCustomWorkDescription] =
    useState('');

  const [customWorkPrice, setCustomWorkPrice] =
    useState('');

  const [customWorkDuration, setCustomWorkDuration] =
    useState('');

  const [customWorkQuantity, setCustomWorkQuantity] =
    useState('1');

  const [workQuantities, setWorkQuantities] =
    useState({});

  /* ================================
     Customer Selection
     ================================ */

  const [allBookings, setAllBookings] =
    useState([]);

  const [selectedCustomerId, setSelectedCustomerId] =
    useState('');

  /* ================================
     Authentication
     ================================ */

  const getAuthHeader = useCallback(async () => {
    const token = await getToken();

    return token
      ? {
          Authorization: `Bearer ${token}`
        }
      : {};
  }, [getToken]);

  /* ================================
     Initial Data
     ================================ */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRemedialWorks();
    fetchEICRSettings();
    fetchPendingApprovals();
    fetchAllBookings();
  }, [getAuthHeader]);

  /* ================================
     Fetch Bookings
     ================================ */

  const fetchAllBookings = async () => {
    try {
      const headers = await getAuthHeader();

      const response = await axios.post(
        `${API_URL}/all-bookings`,
        {},
        {
          headers
        }
      );

      if (
        response.data &&
        Array.isArray(response.data)
      ) {
        setAllBookings(response.data);
      }
    } catch (err) {
      console.error(
        'Error fetching bookings:',
        err
      );

      setError(
        err.message ||
          'Failed to load bookings'
      );
    }
  };

  /* ================================
     Customer Selection
     ================================ */

  const handleCustomerSelect = (
    selectedOption
  ) => {
    if (!selectedOption) {
      setSelectedCustomerId('');
      return;
    }

    const bookingId =
      selectedOption.value;

    setSelectedCustomerId(bookingId);

    const booking = allBookings.find(
      (b) =>
        String(b.bookingID) ===
        String(bookingId)
    );

    if (!booking) {
      return;
    }

    let inspectionDate = '';

    if (booking.date) {
      const d = new Date(
        booking.date
      );

      const yyyy =
        d.getUTCFullYear();

      const mm = String(
        d.getUTCMonth() + 1
      ).padStart(2, '0');

      const dd = String(
        d.getUTCDate()
      ).padStart(2, '0');

      inspectionDate =
        `${yyyy}-${mm}-${dd}`;
    }

    setFormData((prev) => ({
      ...prev,

      customerName:
        booking.customerName || '',

      customerEmail:
        booking.customerEmail || '',

      customerPhone:
        booking.customerNumber || '',

      customerAddress: {
        line1:
          booking.addressLine1 || '',

        line2:
          booking.addressLine2 || '',

        city:
          booking.postTown || '',

        postcode:
          booking.postcode || ''
      },

      inspectionDate,

      propertyAddress:
        prev.sameAsCustomer
          ? {
              line1:
                booking.addressLine1 ||
                '',

              line2:
                booking.addressLine2 ||
                '',

              city:
                booking.postTown || '',

              postcode:
                booking.postcode || ''
            }
          : prev.propertyAddress
    }));
  };

  /* ================================
     Pending Approvals
     ================================ */

  const fetchPendingApprovals =
    async () => {
      try {
        setPendingApprovalsLoading(
          true
        );

        setPendingApprovalsError('');

        const headers =
          await getAuthHeader();

        const response =
          await axios.get(
            `${API_URL}/eicr-records/pending-approval`,
            {
              headers
            }
          );

        if (response.data.success) {
          setPendingApprovals(
            response.data.records || []
          );
        }
      } catch (err) {
        console.error(
          'Error fetching pending EICR approvals:',
          err
        );

        setPendingApprovalsError(
          'Failed to load pending approvals'
        );
      } finally {
        setPendingApprovalsLoading(
          false
        );
      }
    };

  /* ================================
     EICR Settings
     ================================ */

  const fetchEICRSettings =
    async () => {
      try {
        const response =
          await axios.get(
            `${API_URL}/eicr-settings`
          );

        if (response.data.success) {
          setApprovalRequired(
            Boolean(
              response.data
                .eicrApprovalRequired
            )
          );
        }
      } catch (err) {
        console.error(
          'Error fetching EICR settings:',
          err.message
        );
      }
    };

  /* ================================
     Remedial Works
     ================================ */

  const fetchRemedialWorks =
    async () => {
      try {
        const response =
          await axios.get(
            `${API_URL}/remedial-works`
          );

        if (response.data.success) {
          setRemedialWorks(
            response.data.works
          );
        }
      } catch (err) {
        console.error(
          'Error fetching remedial works:',
          err
        );

        setError(
          'Failed to load remedial works'
        );
      }
    };

  /* ================================
     Standard Form Fields
     ================================ */

  const handleInputChange = (e) => {
    const {
      name,
      value,
      checked
    } = e.target;

    if (name === 'sameAsCustomer') {
      setFormData((prev) => ({
        ...prev,

        sameAsCustomer: checked,

        propertyAddress: checked
          ? prev.customerAddress
          : prev.propertyAddress
      }));

      return;
    }

    if (
      name.startsWith('customer') ||
      name.startsWith('property')
    ) {
      const [
        addressType,
        field
      ] =
        name
          .match(
            /^(customer|property)Address_(.+)$/
          )
          ?.slice(1) || [];

      if (addressType) {
        setFormData((prev) => ({
          ...prev,

          [`${addressType}Address`]:
            {
              ...prev[
                `${addressType}Address`
              ],

              [field]: value
            }
        }));

        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  /* ================================
     Standard Remedial Work
     ================================ */

  const handleRemedialWorkChange = (
    workId
  ) => {
    const isCurrentlySelected =
      formData.remedialWorkIds.includes(
        workId
      );

    setFormData((prev) => ({
      ...prev,

      remedialWorkIds:
        prev.remedialWorkIds.includes(
          workId
        )
          ? prev.remedialWorkIds.filter(
              (id) => id !== workId
            )
          : [
              ...prev.remedialWorkIds,
              workId
            ]
    }));

    if (!isCurrentlySelected) {
      setWorkQuantities(
        (prev) => ({
          ...prev,
          [workId]: 1
        })
      );
    }
  };

  /* ================================
     Custom Work
     ================================ */

  const handleAddCustomWork = () => {
    if (
      !customWorkDescription.trim() ||
      !customWorkPrice.trim() ||
      !customWorkDuration.trim()
    ) {
      setError(
        'Please enter work description, price, and duration'
      );

      return;
    }

    const price = parseFloat(
      customWorkPrice
    );

    if (
      Number.isNaN(price) ||
      price <= 0
    ) {
      setError(
        'Please enter a valid price'
      );

      return;
    }

    const durationMins =
      parseInt(
        customWorkDuration,
        10
      );

    if (
      Number.isNaN(durationMins) ||
      durationMins <= 0
    ) {
      setError(
        'Please enter a valid duration in minutes'
      );

      return;
    }

    const quantity =
      parseInt(
        customWorkQuantity,
        10
      );

    if (
      Number.isNaN(quantity) ||
      quantity <= 0
    ) {
      setError(
        'Please enter a valid quantity'
      );

      return;
    }

    const customWork = {
      isCustom: true,

      description:
        customWorkDescription.trim(),

      price,

      durationMins,

      quantity
    };

    setFormData((prev) => ({
      ...prev,

      remedialWorkIds: [
        ...prev.remedialWorkIds,
        customWork
      ]
    }));

    setCustomWorkDescription('');
    setCustomWorkPrice('');
    setCustomWorkDuration('');
    setCustomWorkQuantity('1');

    setError('');
  };

  const handleRemoveCustomWork = (
    customWork
  ) => {
    setFormData((prev) => ({
      ...prev,

      remedialWorkIds:
        prev.remedialWorkIds.filter(
          (item) =>
            typeof item === 'string' ||
            item.description !==
              customWork.description
        )
    }));
  };

  /* ================================
     File Upload
     ================================ */

  const handleFileChange = (e) => {
    const file =
      e.target.files[0];

    if (!file) {
      return;
    }

    if (
      file.type !==
      'application/pdf'
    ) {
      setError(
        'Only PDF files are allowed'
      );

      return;
    }

    if (
      file.size >
      10 * 1024 * 1024
    ) {
      setError(
        'File size must be less than 10MB'
      );

      return;
    }

    setFormData((prev) => ({
      ...prev,
      eicrFile: file
    }));

    setError('');
  };

    /* ================================
     Approve Existing Record
     ================================ */

  const handleApproveRecord = async (recordId) => {
    try {
      setApprovingId(recordId);
      setPendingApprovalsError('');

      const headers = await getAuthHeader();

      const response = await axios.post(
        `${API_URL}/eicr-records/${recordId}/approve`,
        {},
        {
          headers
        }
      );

      if (response.data.success) {
        await fetchPendingApprovals();
      } else {
        setPendingApprovalsError(
          response.data.message ||
            'Failed to approve EICR record'
        );
      }
    } catch (err) {
      console.error(
        'Error approving EICR record:',
        err
      );

      setPendingApprovalsError(
        err.response?.data?.message ||
          'Failed to approve EICR record'
      );
    } finally {
      setApprovingId('');
    }
  };

  /* ================================
     Submit
     ================================ */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setSuccess('');

    if (!formData.eicrFile) {
      setError(
        'Please upload the EICR PDF file'
      );
      return;
    }

    if (
      !formData.customerName.trim()
    ) {
      setError(
        'Customer name is required'
      );
      return;
    }

    if (
      !formData.customerPhone.trim()
    ) {
      setError(
        'Landlord phone number is required'
      );
      return;
    }

    if (
      !formData.customerAddress.line1.trim() ||
      !formData.customerAddress.city.trim() ||
      !formData.customerAddress.postcode.trim()
    ) {
      setError(
        'Please complete the required customer address fields'
      );
      return;
    }

    if (
      !formData.inspectionDate ||
      !formData.expiryDate
    ) {
      setError(
        'Inspection date and expiry date are required'
      );
      return;
    }

    if (
      formData.result === 'Fail' &&
      formData.remedialWorkIds.length === 0
    ) {
      setError(
        'Please select at least one remedial work item for a failed EICR'
      );
      return;
    }

    try {
      setLoading(true);

      /*
       * STEP 1:
       * Upload the PDF.
       */

      const fileFormData =
        new FormData();

      fileFormData.append(
        'file',
        formData.eicrFile
      );

      const uploadResponse =
        await axios.post(
          `${API_URL}/upload-eicr`,
          fileFormData,
          {
            headers: {
              'Content-Type':
                'multipart/form-data'
            }
          }
        );

      if (
        !uploadResponse.data.success
      ) {
        throw new Error(
          'Failed to upload file'
        );
      }

      const {
        fileUrl,
        filePublicId
      } = uploadResponse.data;

      /*
       * STEP 2:
       * Prepare selected remedial works.
       */

      const remedialWorkIds =
        formData.remedialWorkIds.map(
          (item) => {
            if (
              typeof item ===
                'object' &&
              item.isCustom
            ) {
              return {
                isCustom: true,
                description:
                  item.description,
                price: item.price,
                durationMins:
                  item.durationMins,
                quantity:
                  item.quantity || 1
              };
            }

            return {
              workId: item,
              quantity:
                workQuantities[item] ||
                1
            };
          }
        );

      /*
       * STEP 3:
       * Create EICR record.
       */

      const submitData = {
        customerName:
          formData.customerName,

        customerEmail:
          formData.customerEmail,

        customerPhone:
          formData.customerPhone,

        customerAddress:
          formData.customerAddress,

        propertyType:
          formData.propertyType,

        propertyAddress:
          formData.sameAsCustomer
            ? formData.customerAddress
            : formData.propertyAddress,

        inspectionDate:
          formData.inspectionDate,

        expiryDate:
          formData.expiryDate,

        result:
          formData.result,

        eicrFileUrl:
          fileUrl,

        eicrFilePublicId:
          filePublicId,

        originalFileName:
          formData.eicrFile.name,

        remedialWorkIds:
          formData.result === 'Fail'
            ? remedialWorkIds
            : []
      };

      const headers =
        await getAuthHeader();

      const response =
        await axios.post(
          `${API_URL}/eicr-records`,
          submitData,
          {
            headers
          }
        );

      if (response.data.success) {
        setSuccess(
          response.data.message ||
            'EICR record created successfully'
        );

        /*
         * Reset form.
         */

        setFormData({
          customerName: '',
          customerEmail: '',
          customerPhone: '',

          customerAddress: {
            line1: '',
            line2: '',
            city: '',
            postcode: ''
          },

          propertyType:
            'Residential',

          propertyAddress: {
            line1: '',
            line2: '',
            city: '',
            postcode: ''
          },

          sameAsCustomer: true,

          inspectionDate: '',
          expiryDate: '',
          result: 'Pass',

          eicrFile: null,

          remedialWorkIds: []
        });

        setSelectedCustomerId('');

        setWorkQuantities({});

        setCustomWorkDescription('');
        setCustomWorkPrice('');
        setCustomWorkDuration('');
        setCustomWorkQuantity('1');

        setRemedialWorksSearch('');

        const fileInput =
          document.getElementById(
            'eicrFile'
          );

        if (fileInput) {
          fileInput.value = '';
        }

        await fetchPendingApprovals();
      } else {
        setError(
          response.data.message ||
            'Failed to create EICR record'
        );
      }
    } catch (err) {
      console.error(
        'Error submitting EICR:',
        err
      );

      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to create EICR record'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================================
     Render
     ================================ */

  return (
    <>
    <div className="eicr-form-container">
      <div className="form-header">
        <h1>EICR Record Creation</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="eicr-form"
      >
        {/* =========================
            Customer Details
            ========================= */}

        <section className="form-section">
          <h2>Customer Details</h2>

          <div className="form-group full-width">
            <label>
              Select Existing Customer
            </label>

            <Select
              value={
                allBookings.find(
                  (b) =>
                    String(
                      b.bookingID
                    ) ===
                    String(
                      selectedCustomerId
                    )
                )
                  ? {
                      value:
                        selectedCustomerId,

                      label: (() => {
                        const booking =
                          allBookings.find(
                            (b) =>
                              String(
                                b.bookingID
                              ) ===
                              String(
                                selectedCustomerId
                              )
                          );

                        const formattedDate =
                          booking?.date
                            ? new Date(
                                booking.date
                              ).toLocaleDateString(
                                'en-GB',
                                {
                                  year:
                                    'numeric',
                                  month:
                                    'short',
                                  day:
                                    'numeric'
                                }
                              )
                            : 'N/A';

                        return `${booking?.customerName} | ${formattedDate} | ${booking?.productName}`;
                      })()
                    }
                  : null
              }
              onChange={
                handleCustomerSelect
              }
              options={allBookings
                .filter((b) => {
                  const bookingDate =
                    new Date(b.date);

                  const today =
                    new Date();

                  today.setHours(
                    0,
                    0,
                    0,
                    0
                  );

                  return (
                    bookingDate < today
                  );
                })
                .map((b) => {
                  const formattedDate =
                    b.date
                      ? new Date(
                          b.date
                        ).toLocaleDateString(
                          'en-GB',
                          {
                            year:
                              'numeric',
                            month:
                              'short',
                            day:
                              'numeric'
                          }
                        )
                      : 'N/A';

                  return {
                    value:
                      b.bookingID,

                    label: `${b.customerName} | ${formattedDate} | ${b.productName}`
                  };
                })}
              isClearable
              isSearchable
              placeholder="-- Search or select from previous bookings --"
              styles={{
                container: (
                  base
                ) => ({
                  ...base,
                  maxWidth: 500
                })
              }}
            />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>
                Customer Name *
              </label>

              <input
                type="text"
                name="customerName"
                value={
                  formData.customerName
                }
                onChange={
                  handleInputChange
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>

              <input
                type="email"
                name="customerEmail"
                value={
                  formData.customerEmail
                }
                onChange={
                  handleInputChange
                }
              />
            </div>

            <div className="form-group">
              <label>
                Landlord's Phone
                Number*
              </label>

              <input
                type="tel"
                name="customerPhone"
                value={
                  formData.customerPhone
                }
                onChange={
                  handleInputChange
                }
                required
              />
            </div>
          </div>

          <h3>
            Customer Address *
          </h3>

          <div className="form-grid">
            <div className="form-group full-width">
              <label>Line 1 *</label>

              <input
                type="text"
                name="customerAddress_line1"
                value={
                  formData
                    .customerAddress
                    .line1
                }
                onChange={
                  handleInputChange
                }
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Line 2</label>

              <input
                type="text"
                name="customerAddress_line2"
                value={
                  formData
                    .customerAddress
                    .line2
                }
                onChange={
                  handleInputChange
                }
              />
            </div>

            <div className="form-group">
              <label>City *</label>

              <input
                type="text"
                name="customerAddress_city"
                value={
                  formData
                    .customerAddress
                    .city
                }
                onChange={
                  handleInputChange
                }
                required
              />
            </div>

            <div className="form-group">
              <label>
                Postcode *
              </label>

              <input
                type="text"
                name="customerAddress_postcode"
                value={
                  formData
                    .customerAddress
                    .postcode
                }
                onChange={
                  handleInputChange
                }
                required
              />
            </div>
          </div>
        </section>

        {/* =========================
            EICR Details
            ========================= */}

        <section className="form-section">
          <h2>EICR Details</h2>

          <div className="form-grid">
            <div className="form-group">
              <label>
                Inspection Date *
              </label>

              <input
                type="date"
                name="inspectionDate"
                value={
                  formData.inspectionDate
                }
                onChange={
                  handleInputChange
                }
                required
              />
            </div>

            <div className="form-group">
              <label>
                Expiry Date *
              </label>

              <input
                type="date"
                name="expiryDate"
                value={
                  formData.expiryDate
                }
                onChange={
                  handleInputChange
                }
                required
              />
            </div>

            <div className="form-group">
              <label>
                Result *
              </label>

              <select
                name="result"
                value={
                  formData.result
                }
                onChange={
                  handleInputChange
                }
                required
              >
                <option value="Pass">
                  Pass
                </option>

                <option value="Fail">
                  Fail
                </option>
              </select>
            </div>
          </div>
        </section>

        {/* =========================
            Remedial Works
            ========================= */}

        {formData.result ===
          'Fail' && (
          <section className="form-section">
            <h2>
              Remedial Works Required
            </h2>

            <p className="section-note">
              Select all remedial
              works required to make
              the property pass.
              Customer will see these
              items in their quote.
            </p>

            {/* Search */}

            <div className="remedial-search">
              <input
                type="text"
                placeholder="Search remedial works..."
                value={
                  remedialWorksSearch
                }
                onChange={(e) =>
                  setRemedialWorksSearch(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="remedial-or">
              OR
            </div>

            {/* Custom Work */}

            <div className="custom-work-card">
              <h3>
                Add Custom Work
              </h3>

              <div className="custom-work-grid">
                <div className="custom-work-field custom-work-description-field">
                  <label>
                    Work Description
                  </label>

                  <input
                    type="text"
                    placeholder="e.g. Replace consumer unit"
                    value={
                      customWorkDescription
                    }
                    onChange={(e) =>
                      setCustomWorkDescription(
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="custom-work-field">
                  <label>
                    Price (£)
                  </label>

                  <input
                    type="number"
                    placeholder="e.g. 250"
                    value={
                      customWorkPrice
                    }
                    onChange={(e) =>
                      setCustomWorkPrice(
                        e.target.value
                      )
                    }
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="custom-work-field">
                  <label>
                    Duration (mins)
                  </label>

                  <input
                    type="number"
                    placeholder="e.g. 60"
                    value={
                      customWorkDuration
                    }
                    onChange={(e) =>
                      setCustomWorkDuration(
                        e.target.value
                      )
                    }
                    step="1"
                    min="1"
                  />
                </div>

                <div className="custom-work-field">
                  <label>
                    Quantity
                  </label>

                  <input
                    type="number"
                    placeholder="1"
                    value={
                      customWorkQuantity
                    }
                    onChange={(e) =>
                      setCustomWorkQuantity(
                        e.target.value
                      )
                    }
                    step="1"
                    min="1"
                  />
                </div>

                <button
                  type="button"
                  className="custom-work-add-btn"
                  onClick={
                    handleAddCustomWork
                  }
                >
                  Add
                </button>
              </div>
            </div>

            {/* =====================
                Standard work list
                ===================== */}

            <div className="remedial-works-container">
              {Object.entries(
                remedialWorks
              ).length === 0 ? (
                <p>
                  Loading remedial
                  works...
                </p>
              ) : (
                Object.entries(
                  remedialWorks
                )
                  .map(
                    ([
                      category,
                      works
                    ]) => {
                      const filteredWorks =
                        works.filter(
                          (work) =>
                            work.description
                              .toLowerCase()
                              .includes(
                                remedialWorksSearch.toLowerCase()
                              )
                        );

                      if (
                        filteredWorks.length ===
                        0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={
                            category
                          }
                          className="category-group"
                        >
                          <h3>
                            {
                              category
                            }
                          </h3>

                          <div className="works-list">
                            {filteredWorks.map(
                              (
                                work
                              ) => {
                                const isSelected =
                                  formData.remedialWorkIds.includes(
                                    work._id
                                  );

                                return (
                                  <div
                                    key={
                                      work._id
                                    }
                                    className={`work-row ${
                                      isSelected
                                        ? 'selected'
                                        : ''
                                    }`}
                                  >
                                    <label className="work-item">
                                      <input
                                        type="checkbox"
                                        checked={
                                          isSelected
                                        }
                                        onChange={() =>
                                          handleRemedialWorkChange(
                                            work._id
                                          )
                                        }
                                      />

                                      <span className="work-description">
                                        {
                                          work.description
                                        }
                                      </span>
                                    </label>

                                    <span className="work-duration">
                                      {getWorkDurationLabel(
                                        work
                                      ) ||
                                        '—'}
                                    </span>

                                    <div className="work-quantity-slot">
                                      {isSelected && (
                                        <div className="work-quantity-control">
                                          <span className="work-quantity-label">
                                            Qty
                                          </span>

                                          <input
                                          className="work-quantity-input"
                                            type="number"
                                            min="1"
                                            max="99"
                                            value={
                                              workQuantities[
                                                work
                                                  ._id
                                              ] ||
                                              1
                                            }
                                            onChange={(
                                              e
                                            ) => {
                                              const newQty =
                                                Math.max(
                                                  1,
                                                  parseInt(
                                                    e
                                                      .target
                                                      .value,
                                                    10
                                                  ) ||
                                                    1
                                                );

                                              setWorkQuantities(
                                                (
                                                  prev
                                                ) => ({
                                                  ...prev,

                                                  [work._id]:
                                                    newQty
                                                })
                                              );
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      );
                    }
                  )
                  .filter(Boolean)
              )}
            </div>

            {/* =====================
                Selected tags
                ===================== */}

            {formData
              .remedialWorkIds
              .length > 0 && (
              <div className="selected-works">
                <div className="selected-works-header">
                  <h3>
                    Selected Items
                  </h3>

                  <span className="selected-works-count">
                    {
                      formData
                        .remedialWorkIds
                        .length
                    }
                  </span>
                </div>

                <div className="selected-work-tags">
                  {formData.remedialWorkIds.map(
                    (
                      item,
                      index
                    ) => {
                      /*
                       * Custom work tag
                       */

                      if (
                        typeof item ===
                          'object' &&
                        item.isCustom
                      ) {
                        return (
                          <div
                            key={`custom-${index}`}
                            className="selected-work-tag custom"
                          >
                            <span className="selected-tag-description">
                              {
                                item.description
                              }
                            </span>

                            <span className="selected-tag-qty">
                              Qty{' '}
                              {item.quantity ||
                                1}
                            </span>

                            <button
                              type="button"
                              className="selected-tag-remove"
                              onClick={() =>
                                handleRemoveCustomWork(
                                  item
                                )
                              }
                              aria-label={`Remove ${item.description}`}
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        );
                      }

                      /*
                       * Standard work tag
                       */

                      const workId =
                        item;

                      let workItem =
                        null;

                      for (
                        const works of Object.values(
                          remedialWorks
                        )
                      ) {
                        const found =
                          works.find(
                            (
                              work
                            ) =>
                              work._id ===
                              workId
                          );

                        if (found) {
                          workItem =
                            found;
                          break;
                        }
                      }

                      if (
                        !workItem
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={
                            workId
                          }
                          className="selected-work-tag"
                        >
                          <span className="selected-tag-description">
                            {
                              workItem.description
                            }
                          </span>

                          <span className="selected-tag-qty">
                            Qty{' '}
                            {workQuantities[
                              workId
                            ] || 1}
                          </span>

                          <button
                            type="button"
                            className="selected-tag-remove"
                            onClick={() =>
                              handleRemedialWorkChange(
                                workId
                              )
                            }
                            aria-label={`Remove ${workItem.description}`}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {/* =========================
            EICR File
            ========================= */}

        <section className="form-section">
          <h2>EICR File</h2>

          <div className="file-upload-area">
            <label
              htmlFor="eicrFile"
              className="file-label"
            >
              <div className="file-icon">
                📄
              </div>

              <div className="file-text">
                <p>
                  Click to upload EICR
                  PDF file
                </p>

                <span>
                  PDF files only, max
                  10MB
                </span>
              </div>
            </label>

            <input
              id="eicrFile"
              type="file"
              accept=".pdf"
              onChange={
                handleFileChange
              }
              style={{
                display: 'none'
              }}
            />

            {formData.eicrFile && (
              <div className="file-selected">
                ✓{' '}
                {
                  formData.eicrFile
                    .name
                }
              </div>
            )}
          </div>
        </section>

        {/* =========================
            Submit
            ========================= */}

        <div className="form-actions">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading
              ? 'Creating Record...'
              : 'Create EICR Record & Send'}
          </button>
        </div>
      </form>
    </div>
      <BottomSpacer/>
      </>
  );
};

export default EICRForm;